import { fromEntries } from "~/tools/from-entries";
import { isString } from "~/tools/is-string";

import type { FormatterOptions } from "./_types";
import { ALL_FORMATTERS, type Formatter } from "./all-formatters";

/**
 * The formatter units are used by customers in their custom configurations,
 * so both 'id' and 'name' should be supported for backward compatibility.
 */
export type FormatterUnit = Formatter["id"] | Formatter["name"];

export const DEFAULT_FORMATTER_UNIT = "short" satisfies FormatterUnit;

type FormattersMap = {
	[Id in FormatterUnit]: Extract<Formatter, { id: Id } | { name: Id }>;
};

const formattersMap = fromEntries(
	ALL_FORMATTERS.flatMap((formatter) => [
		[formatter.id, formatter],
		[formatter.name, formatter],
	]),
) as FormattersMap;

export function isFormatterUnit(input: unknown): input is FormatterUnit {
	return isString(input) && input in formattersMap;
}

export function toFormatterUnit(
	input: undefined | string,
	fallback: FormatterUnit = DEFAULT_FORMATTER_UNIT,
): FormatterUnit {
	return input != null && isFormatterUnit(input) ? input : fallback;
}

export function toFormatter(unit: undefined | string, fallback: FormatterUnit = DEFAULT_FORMATTER_UNIT): Formatter {
	return formattersMap[toFormatterUnit(unit, fallback)];
}

export function formatUnits(unit: undefined | string, value: number): string;
export function formatUnits<TFormatterUnit extends FormatterUnit>(
	unit: TFormatterUnit,
	...[value, options]: Parameters<Extract<Formatter, { id: TFormatterUnit }>["transform"]>
): string;
export function formatUnits(unit: string, value: number, options?: FormatterOptions): string {
	return toFormatter(unit).transform(value, options);
}
